import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { getEmailDirection } from "./threadGrouping";

const API = process.env.REACT_APP_API_URL;

/**
 * Diálogo para enlazar el email actual a un presupuesto del CRM.
 */
export default function EmailEnlazarPresupuesto({
  open,
  email,
  onClose,
  onLinked,
}) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [party, setParty] = useState("supplier");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const direction = useMemo(
    () => getEmailDirection(email),
    [email],
  );

  useEffect(() => {
    if (!open) return;
    setQ("");
    setResults([]);
    setSelected(null);
    setError("");
    setOkMsg("");
    setParty("supplier");
  }, [open, direction]);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(
          `${API}/crm-bridge/presupuestos/search`,
          { params: { q: term, limit: 25 } },
        );
        if (!cancelled) setResults(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) {
          setResults([]);
          setError(
            err?.response?.data?.error ||
              err?.message ||
              "No se pudieron buscar presupuestos",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, open]);

  const handleLink = async () => {
    if (!email?.gmailId || !selected?.IdPresupuesto) return;
    setLinking(true);
    setError("");
    setOkMsg("");
    try {
      const { data } = await axios.post(`${API}/crm-bridge/estimate-comms/link`, {
        IdPresupuesto: selected.IdPresupuesto,
        gmailId: email.gmailId,
        threadId: email.threadId || null,
        subject: email.subject || "",
        from: email.from || "",
        to: email.to_email || email.to || "",
        snippet: email.snippet || "",
        direction,
        party,
        tipo: "manual",
        date: email.date || null,
      });
      if (data?.alreadyLinked) {
        setOkMsg("Este email ya estaba enlazado a ese presupuesto.");
      } else {
        setOkMsg(
          `Enlazado a ${selected.NumeroPresupuesto || selected.IdPresupuesto}.`,
        );
      }
      onLinked?.(data);
      setTimeout(() => onClose?.(), 700);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "No se pudo enlazar el email",
      );
    } finally {
      setLinking(false);
    }
  };

  return (
    <Dialog open={open} onClose={linking ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Enlazar a presupuesto</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {email?.subject || "(Sin asunto)"}
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        ) : null}
        {okMsg ? (
          <Alert severity="success" sx={{ mb: 1.5 }}>
            {okMsg}
          </Alert>
        ) : null}

        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Buscar presupuesto"
          placeholder="P-…, nombre, proyecto, diseño…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ mb: 1.5 }}
        />

        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel id="link-party-label">Contraparte</InputLabel>
          <Select
            labelId="link-party-label"
            label="Contraparte"
            value={party}
            onChange={(e) => setParty(e.target.value)}
          >
            <MenuItem value="supplier">Proveedor</MenuItem>
            <MenuItem value="client">Cliente</MenuItem>
            <MenuItem value="internal">Interno</MenuItem>
          </Select>
        </FormControl>

        <Box
          sx={{
            minHeight: 180,
            maxHeight: 280,
            overflow: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : results.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 2 }}
            >
              {q.trim().length < 2
                ? "Escribe al menos 2 caracteres para buscar."
                : "Sin resultados."}
            </Typography>
          ) : (
            <List dense disablePadding>
              {results.map((row) => (
                <ListItemButton
                  key={row.IdPresupuesto}
                  selected={selected?.IdPresupuesto === row.IdPresupuesto}
                  onClick={() => setSelected(row)}
                >
                  <ListItemText
                    primary={`${row.NumeroPresupuesto || row.IdPresupuesto}${
                      row.Estado ? ` · ${row.Estado}` : ""
                    }`}
                    secondary={[
                      row.NombrePresupuesto,
                      row.ProyectoNombre,
                      row.ProveedorNombre,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={linking}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleLink}
          disabled={linking || !selected}
        >
          {linking ? "Enlazando…" : "Enlazar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
